// Phase 5 (web revival) — /album/$albumID timeline + rename header.
//
// Same TimelineGrid as /library, bound to /v1/albums/{id}/assets which
// also paginates by cursor and returns AssetListResponse. The header
// shows the album name + asset count and a "Edit name" affordance that
// flips an inline input → PATCH /v1/albums/{id}.

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { AlbumDTO, AlbumListResponse, AssetListResponse } from '../api/types';
import { qk } from '../app/queryClient';
import { setLightboxSource } from '../app/lightboxNav';
import { TimelineGrid } from '../components/timeline/TimelineGrid';

function useAlbumSummary(albumID: string) {
  // We piggy-back on the cached AlbumListResponse — every album lookup
  // goes through GET /v1/albums first, so reading the cache here keeps
  // us off a redundant single-album fetch. If the list isn't cached
  // (deep link, hard refresh), we fall through to the network call.
  const qc = useQueryClient();
  const cached = qc
    .getQueryData<AlbumListResponse>(qk.albums())
    ?.albums.find((a) => a.id === albumID);
  return useQuery({
    queryKey: qk.album(albumID),
    queryFn: async () => {
      const list = await api.get<AlbumListResponse>('/v1/albums');
      const found = list.albums.find((a) => a.id === albumID);
      if (!found) throw new Error('Album not found');
      return found;
    },
    initialData: cached,
  });
}

function RenameRow({
  album,
  onDone,
}: {
  album: AlbumDTO;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(album.name);
  const patch = useMutation({
    mutationFn: (n: string) =>
      api.patch<AlbumDTO>(`/v1/albums/${album.id}`, { name: n }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.albums() });
      void qc.invalidateQueries({ queryKey: qk.album(album.id) });
      onDone();
    },
  });

  return (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) patch.mutate(name.trim());
          else if (e.key === 'Escape') onDone();
        }}
        className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm text-neutral-100 outline-none focus:border-neutral-500"
      />
      <button
        type="button"
        disabled={!name.trim() || patch.isPending}
        onClick={() => patch.mutate(name.trim())}
        className="rounded-md bg-neutral-200 px-2 py-1 text-xs font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
      >
        {patch.isPending ? '…' : 'Save'}
      </button>
      <button
        type="button"
        onClick={onDone}
        className="rounded-md px-2 py-1 text-xs text-neutral-400 hover:bg-neutral-800"
      >
        Cancel
      </button>
      {patch.isError && (
        <span className="text-xs text-red-400">
          {(patch.error as Error).message}
        </span>
      )}
    </div>
  );
}

export function AlbumDetailPage({ albumID }: { albumID: string }) {
  const [renaming, setRenaming] = useState(false);
  const summary = useAlbumSummary(albumID);

  // Close the rename row if the album swaps under us (route param
  // changes via in-place navigation).
  useEffect(() => {
    setRenaming(false);
  }, [albumID]);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | null }) =>
      api.get<AssetListResponse>(`/v1/albums/${albumID}/assets`, {
        cursor: pageParam ?? undefined,
        limit: 100,
      }),
    [albumID],
  );

  const assets = useInfiniteQuery({
    queryKey: qk.albumAssets(albumID),
    initialPageParam: null as string | null,
    queryFn,
    getNextPageParam: (page) => page.next_cursor,
  });

  // Source the lightbox's prev/next + forward pagination from this album.
  useEffect(() => {
    setLightboxSource({
      queryKey: qk.albumAssets(albumID),
      initialPageParam: null,
      queryFn,
      getNextPageParam: (page) => page.next_cursor,
    });
  }, [albumID, queryFn]);

  const flatAssets = useMemo(
    () => (assets.data?.pages ?? []).flatMap((p) => p.assets),
    [assets.data],
  );

  return (
    <div className="flex h-full flex-col bg-neutral-950">
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-white/5 px-4">
        <div className="flex items-center gap-3">
          {summary.data && renaming ? (
            <RenameRow album={summary.data} onDone={() => setRenaming(false)} />
          ) : (
            <>
              <h1 className="font-display text-[17px] font-medium tracking-tight text-neutral-100">
                {summary.data?.name ?? '…'}
              </h1>
              <span className="text-xs text-neutral-500">
                {summary.data
                  ? `${summary.data.asset_count} ${summary.data.asset_count === 1 ? 'photo' : 'photos'}`
                  : ''}
              </span>
            </>
          )}
        </div>
        {summary.data && !renaming && (
          <button
            type="button"
            onClick={() => setRenaming(true)}
            aria-label="Rename album"
            title="Rename album"
            className="flex size-7 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-white/10 hover:text-neutral-200"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
          </button>
        )}
      </header>
      <div className="flex-1 overflow-hidden">
        {assets.isError ? (
          <div className="flex h-full items-center justify-center text-sm text-red-400">
            Failed to load: {(assets.error as Error).message}
          </div>
        ) : assets.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-neutral-500">
            Loading…
          </div>
        ) : (
          <TimelineGrid
            assets={flatAssets}
            hasNextPage={assets.hasNextPage}
            isFetchingNextPage={assets.isFetchingNextPage}
            fetchNextPage={() => void assets.fetchNextPage()}
            scrollKey={`album:${albumID}`}
            emptyLabel="This album is empty."
          />
        )}
      </div>
    </div>
  );
}
